import { 
    FlightChangeDetail, 
    DetailHeaderViewModel,
    Flight,
    Itinerary,
    ChangeReason
} from '../types/detailInterfaces';
import { 
    FlightChangeDetailRawResponse,
    FlightRaw,
    ItineraryRaw,
    ChangeReasonRaw
} from '../types/detailRawInterfaces';
import { formatDateTime } from '../../../../utils/date';
import { caseStatusMapper } from './statusMapper';

/**
 * Maps raw API response to FlightChangeDetail domain model
 * Note: status is kept as raw string since StatusChip component handles the mapping
 */
export class FlightChangeDetailMapper {
    private mapFlightRawToFlight(flightRaw: FlightRaw): Flight {
        const parsedPieces = parseInt(flightRaw.pieces, 10);
        const piecesAssigned = isNaN(parsedPieces) || parsedPieces < 0 
            ? (() => {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`Invalid pieces value: "${flightRaw.pieces}", defaulting to 0`);
                }
                return 0;
            })()
            : parsedPieces;

        return {
            fromAirport: flightRaw.from,
            toAirport: flightRaw.to,
            piecesAssigned,
            flightNumber: flightRaw.flightNo,
            etd: flightRaw.etd,
            eta: flightRaw.eta,
            flown: flightRaw.flown ?? null,
            leg: flightRaw.leg
        };
    }

    private mapItineraryRawToItinerary(itineraryRaw: ItineraryRaw): Itinerary {
        return {
            flights: (itineraryRaw.flights || []).map(flight => this.mapFlightRawToFlight(flight)),
            pieces: itineraryRaw.pieces
        };
    }

    private mapChangeReasonRawToChangeReason(changeReasonRaw: ChangeReasonRaw): ChangeReason {
        return {
            initialReasonCodeId: changeReasonRaw.initialReasonCodeId,
            isInitialReasonCodeCorrect: changeReasonRaw.isInitialReasonCodeCorrect,
            newReasonCodeId: changeReasonRaw.newReasonCodeId,
            remarkId: changeReasonRaw.newRemarkId ?? changeReasonRaw.initialRemarkId ?? null
        };
    }

    mapSingle(raw: FlightChangeDetailRawResponse): FlightChangeDetail {
        let mappedStatus: string = raw.status;
        try {
            mappedStatus = caseStatusMapper.map(raw.status);
        } catch {
            console.warn(`Could not map status: ${raw.status}, using raw value`);
        }

        return {
            id: raw.id,
            awb: raw.awb,
            status: mappedStatus,
            notificationStatus: raw.notificationStatus,
            analyst: raw.analyst ? {
                id: raw.analyst.id,
                name: raw.analyst.name
            } : null,
            company: raw.company ?? '',
            branchNumber: raw.branchNumber,
            totalPieces: raw.totalPieces,
            originalRoute: raw.originalRoute,
            destinationRoute: raw.destinationRoute,
            itineraryChange: {
                originalItinerary: this.mapItineraryRawToItinerary(raw.itineraryChange.originalItinerary),
                newItinerary: this.mapItineraryRawToItinerary(raw.itineraryChange.newItinerary),
                ...(raw.itineraryChange.transit && { transit: raw.itineraryChange.transit })
            },
            changeReason: this.mapChangeReasonRawToChangeReason(raw.changeReason),
            notes: raw.notes,
            reasonCodes: raw.reasonCodes || [],
            remarks: raw.remarks || [],
            contacts: raw.contacts || [],
            reasonNotApplicable: raw.reasonNotApplicable || '',
            automationFeedback: raw.automationFeedback ?? null,
        };
    }
}

/**
 * Maps FlightChangeDetail to DetailHeader component props
 */
export const mapToDetailHeaderProps = (
    data: FlightChangeDetail
): DetailHeaderViewModel => {
    const firstFlight = data.itineraryChange?.newItinerary?.flights?.[0];
    const etdFormatted = firstFlight?.etd 
        ? formatDateTime(firstFlight.etd, 'es-ES', false)
        : { date: '-', time: '-' };

    return {
        title: data.awb,
        companyName: data.company,
        branchNumber: data.branchNumber,
        codes: data.awb, 
        route: `${data.originalRoute}-${data.destinationRoute}`,
        etd: `${etdFormatted.date}, ${etdFormatted.time}`,
        pieces: data.totalPieces,
        notificationStatus: data.notificationStatus,
        analystName: data.analyst?.name ?? '',
        status: data.status as string,
        reasonCodes: data.reasonCodes,
        remarks: data.remarks,
        contacts: data.contacts,
    };
};

/**
 * Maps raw API response directly to DetailHeader props (convenience function)
 */
export const mapRawToDetailHeaderProps = (
    raw: FlightChangeDetailRawResponse
): DetailHeaderViewModel => {
    const mapper = new FlightChangeDetailMapper();
    const detail = mapper.mapSingle(raw);
    return mapToDetailHeaderProps(detail);
};

export const flightChangeDetailMapper = new FlightChangeDetailMapper();

